/**
 * Google Ads click identifiers (gclid / gbraid / wbraid).
 *
 * Firestore has no rigid schema. `clickIdentifiers` is an additive field:
 * new visits write it, existing orders are not rewritten. A record without
 * `capturedAt` has an unknown click time and must not be treated as fresh
 * or backfilled with the upload time.
 */

export const GOOGLE_ADS_CLICK_LOOKBACK_MS = 90 * 24 * 60 * 60 * 1000

export const GOOGLE_ADS_CLICK_ID_TYPES = ["gclid", "gbraid", "wbraid"] as const

export type GoogleAdsClickIdType = (typeof GOOGLE_ADS_CLICK_ID_TYPES)[number]

export interface GoogleAdsStoredConsent {
  analytics_storage?: "granted" | "denied"
  ad_storage?: "granted" | "denied"
  ad_user_data?: "granted" | "denied"
  ad_personalization?: "granted" | "denied"
}

export interface GoogleAdsClickIdentifier {
  type: GoogleAdsClickIdType
  /** Raw click ID. Never trimmed, rewritten, or generated. */
  value: string
  /** ISO-8601 time of the first observation. Missing means unknown age. */
  capturedAt?: string
  source?: string | null
  medium?: string | null
  consent?: GoogleAdsStoredConsent
}

export interface GoogleAdsClickAnalytics {
  gclid?: string
  gbraid?: string
  wbraid?: string
  clickIdentifiers?: GoogleAdsClickIdentifier[]
  consent?: GoogleAdsStoredConsent
}

const MAX_STORED_CLICK_IDENTIFIERS = 20

export function parseClickCapturedAt(value: string | undefined): Date | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
}

/**
 * Copy legacy single fields into the list without inventing `capturedAt`.
 * Values already stored with a timestamp are left untouched.
 */
export function coalesceClickIdentifiers(
  analytics: GoogleAdsClickAnalytics | null | undefined
): GoogleAdsClickIdentifier[] {
  const existing = Array.isArray(analytics?.clickIdentifiers)
    ? analytics!.clickIdentifiers!.filter(isWellFormedIdentifier)
    : []
  const known = new Set(existing.map(id => `${id.type}:${id.value}`))
  const legacy: GoogleAdsClickIdentifier[] = []

  for (const type of GOOGLE_ADS_CLICK_ID_TYPES) {
    const value = analytics?.[type]
    if (typeof value !== "string" || value.length === 0) continue
    const key = `${type}:${value}`
    if (known.has(key)) continue
    legacy.push({ type, value })
  }

  return [...existing, ...legacy]
}

export function mergeClickIdentifiers(params: {
  existing: GoogleAdsClickIdentifier[] | undefined
  observed: Array<{ type: GoogleAdsClickIdType; value: string }>
  observedAt: Date
  source?: string | null
  medium?: string | null
  consent?: GoogleAdsStoredConsent
}): { identifiers: GoogleAdsClickIdentifier[]; changed: boolean } {
  const identifiers = (params.existing ?? []).filter(isWellFormedIdentifier)
  let changed = identifiers.length !== (params.existing ?? []).length

  for (const observation of params.observed) {
    if (!observation?.value) continue
    const already = identifiers.find(
      id => id.type === observation.type && id.value === observation.value
    )
    if (already) {
      // Same click. Keep the original capture time, including a missing one.
      continue
    }

    identifiers.push({
      type: observation.type,
      value: observation.value,
      capturedAt: params.observedAt.toISOString(),
      source: params.source ?? null,
      medium: params.medium ?? null,
      ...(params.consent ? { consent: params.consent } : {}),
    })
    changed = true
  }

  const trimmed = trimToNewest(identifiers)
  if (trimmed.length !== identifiers.length) changed = true
  return { identifiers: trimmed, changed }
}

export type ClickSelection =
  | {
      status: "selected"
      type: GoogleAdsClickIdType
      value: string
      capturedAt: Date
      ageMs: number
    }
  | {
      status: "rejected"
      reason: "no_click_id" | "click_time_unknown" | "expired_click" | "click_after_conversion"
      permanent: boolean
      identifierType: GoogleAdsClickIdType | null
      ageMs: number | null
    }

/**
 * Newest click ID that is not after the conversion and is strictly inside
 * the 90-day click-through window. An older valid ID is kept when a newer
 * one is unusable. IDs without a trustworthy capture time are ignored.
 */
export function selectClickIdentifier(params: {
  analytics: GoogleAdsClickAnalytics | null | undefined
  conversionAt: Date
}): ClickSelection {
  const identifiers = coalesceClickIdentifiers(params.analytics)
  if (identifiers.length === 0) {
    return {
      status: "rejected",
      reason: "no_click_id",
      permanent: false,
      identifierType: null,
      ageMs: null,
    }
  }

  const timed = identifiers
    .map(id => {
      const capturedAt = parseClickCapturedAt(id.capturedAt)
      return capturedAt ? { id, capturedAt } : null
    })
    .filter((entry): entry is { id: GoogleAdsClickIdentifier; capturedAt: Date } => !!entry)
    .sort((a, b) => b.capturedAt.getTime() - a.capturedAt.getTime())

  if (timed.length === 0) {
    return {
      status: "rejected",
      reason: "click_time_unknown",
      permanent: false,
      identifierType: identifiers[identifiers.length - 1]?.type ?? null,
      ageMs: null,
    }
  }

  for (const candidate of timed) {
    const ageMs = params.conversionAt.getTime() - candidate.capturedAt.getTime()
    if (ageMs < 0) continue
    if (ageMs >= GOOGLE_ADS_CLICK_LOOKBACK_MS) continue
    return {
      status: "selected",
      type: candidate.id.type,
      value: candidate.id.value,
      capturedAt: candidate.capturedAt,
      ageMs,
    }
  }

  const newest = timed[0]
  const newestAge = params.conversionAt.getTime() - newest.capturedAt.getTime()
  if (newestAge < 0) {
    return {
      status: "rejected",
      reason: "click_after_conversion",
      permanent: true,
      identifierType: newest.id.type,
      ageMs: newestAge,
    }
  }

  return {
    status: "rejected",
    reason: "expired_click",
    permanent: true,
    identifierType: newest.id.type,
    ageMs: newestAge,
  }
}

export function explicitConsentStatus(
  value: "granted" | "denied" | undefined
): "GRANTED" | "DENIED" | undefined {
  if (value === "granted") return "GRANTED"
  if (value === "denied") return "DENIED"
  return undefined
}

function isWellFormedIdentifier(
  id: GoogleAdsClickIdentifier | null | undefined
): id is GoogleAdsClickIdentifier {
  return !!id && (id.type === "gclid" || id.type === "gbraid" || id.type === "wbraid") && typeof id.value === "string" && id.value.length > 0
}

function trimToNewest(identifiers: GoogleAdsClickIdentifier[]): GoogleAdsClickIdentifier[] {
  if (identifiers.length <= MAX_STORED_CLICK_IDENTIFIERS) return identifiers
  const ranked = identifiers
    .map((id, index) => ({
      id,
      index,
      time: parseClickCapturedAt(id.capturedAt)?.getTime() ?? Number.NEGATIVE_INFINITY,
    }))
    .sort((a, b) => a.time - b.time || a.index - b.index)
  const drop = new Set(
    ranked.slice(0, identifiers.length - MAX_STORED_CLICK_IDENTIFIERS).map(entry => entry.id)
  )
  return identifiers.filter(id => !drop.has(id))
}
